"use client";

import React, { useEffect, useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import usePageTitle from '@/hooks/usePageTitle';
import JsonLd from '@/components/SEO/JsonLd';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Script from 'next/script';

const contactSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  email: z.string().trim().email('Please enter a valid email address').max(255),
  organization: z.string().trim().min(1, 'Organization name is required').max(200),
  orgType: z.string().min(1, 'Please select an organization type'),
  message: z.string().trim().min(1, 'Please tell us about your project').max(2000),
  budget: z.string().min(1, 'Please select a budget range'),
  timeline: z.string().optional(),
  source: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;



import { getConsent } from '@/lib/consent';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Calendar } from 'lucide-react';

function BookContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isFunctionalAllowed, setIsFunctionalAllowed] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const defaultMode = 'book';
  const [activeTab, setActiveTab] = useState(defaultMode);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  useEffect(() => {
    const checkConsent = () => {
      const consent = getConsent();
      setIsFunctionalAllowed(consent?.functional ?? false);
    };

    checkConsent();
    window.addEventListener("consentUpdated", checkConsent);
    return () => window.removeEventListener("consentUpdated", checkConsent);
  }, []);

  const onFormSubmit = async (data: ContactFormData) => {
    console.log('Form submitted:', data);
    toast.success('Message sent! We\'ll be in touch within 2 business days.');
    router.push('/thank-you');
  };

  const openPrivacySettings = () => {
    window.dispatchEvent(new CustomEvent("openPrivacyModal"));
  };

  useEffect(() => {
    // Manually initialize the widget when the tab is switched to 'book',
    // consent is granted, and the script is loaded.
    const widgetContainer = document.querySelector('.calendly-inline-widget');
    
    if (activeTab === 'book' && isFunctionalAllowed && isScriptLoaded && (window as any).Calendly && widgetContainer) {
      // Clear container to prevent duplicate widgets
      widgetContainer.innerHTML = '';
      
      (window as any).Calendly.initInlineWidget({
        url: 'https://calendly.com/laurelwebco/discovery-call?hide_event_type_details=1&hide_gdpr_banner=1&background_color=edece6&primary_color=2d4a2d',
        parentElement: widgetContainer,
        prefill: {},
        utm: {}
      });
    }
  }, [activeTab, isFunctionalAllowed, isScriptLoaded]);

  return (
    <>
      {isFunctionalAllowed && (
        <Script 
          src="https://assets.calendly.com/assets/external/widget.js" 
          strategy="afterInteractive"
          onLoad={() => setIsScriptLoaded(true)}
        />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        {/* Left Column: Context/Details */}
        <div className="max-w-xl">
          <h2 className="heading-section text-bloom-charcoal mb-8">What to expect</h2>
          <ul className="space-y-4 mb-10" role="list">
            {[
              "Share your mission and current website challenges",
              "Discuss your audience and technical constraints",
              "Identify one goal your site isn't meeting today"
            ].map((point, i) => (
              <li key={i} className="flex items-start gap-3 font-sans font-light text-bloom-charcoal text-lg">
                <span className="w-1.5 h-1.5 rounded-full bg-bloom-gold mt-3 flex-shrink-0" aria-hidden="true" />
                {point}
              </li>
            ))}
          </ul>

          <div className="w-24 h-px bg-bloom-gold/30 mb-6" aria-hidden="true" />
          
          <p className="font-sans text-sm text-bloom-muted italic">
            Responds within 2 business days. All consultations are via Google Meet.
          </p>
        </div>

        {/* Right Column: Interaction Toggle */}
        <div className="relative w-full max-w-2xl mx-auto lg:mx-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="flex w-full mb-12 bg-transparent border-b border-bloom-border rounded-none p-0 h-auto">
              <TabsTrigger 
                value="book" 
                className="flex-1 rounded-none border-b-2 border-transparent py-4 data-[state=active]:border-bloom-green-deep data-[state=active]:bg-transparent data-[state=active]:text-bloom-green-deep text-bloom-muted hover:text-bloom-charcoal transition-all font-sans font-bold uppercase tracking-widest text-xs"
              >
                1. Book a Call
              </TabsTrigger>
              <TabsTrigger 
                value="message"
                className="flex-1 rounded-none border-b-2 border-transparent py-4 data-[state=active]:border-bloom-green-deep data-[state=active]:bg-transparent data-[state=active]:text-bloom-green-deep text-bloom-muted hover:text-bloom-charcoal transition-all font-sans font-bold uppercase tracking-widest text-xs"
              >
                2. Send a Message
              </TabsTrigger>
            </TabsList>

            {/* Mode 1: Calendly (Temporarily Paused) */}
            <div className={activeTab === 'book' ? 'block' : 'hidden'} role="tabpanel" style={{ height: '700px' }}>
              <div className="w-full h-full flex items-center justify-center">
                {isFunctionalAllowed ? (
                  <div 
                    className="calendly-inline-widget w-full h-full" 
                    data-url="https://calendly.com/laurelwebco/discovery-call?hide_event_type_details=1&hide_gdpr_banner=1&background_color=edece6&primary_color=2d4a2d" 
                    style={{ minWidth: '320px', height: '100%' }}
                  />
                ) : (
                  <div className="max-w-sm text-center p-8 animate-in fade-in zoom-in duration-500">
                    <div className="w-16 h-16 bg-bloom-green-deep/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <ShieldCheck className="w-8 h-8 text-bloom-green-deep" />
                    </div>
                    <h3 className="font-serif text-xl text-bloom-charcoal mb-3">Privacy First</h3>
                    <p className="font-sans text-sm text-bloom-muted mb-8 leading-relaxed">
                      To protect your privacy, the scheduler only loads after you've granted permission for third-party scripts.
                    </p>
                    <Button 
                      onClick={openPrivacySettings}
                      className="bg-bloom-green-deep text-white hover:bg-black px-8 py-6 rounded-xl h-auto"
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Manage Privacy Preferences
                    </Button>
                  </div>
                )}
              </div>
            </div>

          {/* Mode 2: Contact Form */}
          <div className={activeTab === 'message' ? 'block' : 'hidden'} role="tabpanel" style={{ height: '700px' }}>
            <div className="w-full h-full overflow-y-auto scrollbar-hide py-4">
              <p className="text-xs text-bloom-muted mb-6 italic font-sans">* indicates a required field</p>
              <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4" noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-bloom-muted mb-1 uppercase tracking-wider">Name *</label>
                    <input
                      id="name"
                      {...register('name')}
                      aria-required="true"
                      aria-invalid={errors.name ? 'true' : 'false'}
                      className="w-full px-4 py-3 rounded-lg border border-bloom-border bg-bloom-cream/20 font-sans text-sm focus-bloom"
                      placeholder="Your Name"
                    />
                    {errors.name && <p className="text-xs text-destructive mt-1 uppercase tracking-tight font-medium" role="alert">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-bloom-muted mb-1 uppercase tracking-wider">Email *</label>
                    <input
                      id="email"
                      type="email"
                      {...register('email')}
                      aria-required="true"
                      aria-invalid={errors.email ? 'true' : 'false'}
                      className="w-full px-4 py-3 rounded-lg border border-bloom-border bg-bloom-cream/20 font-sans text-sm focus-bloom"
                      placeholder="email@organization.org"
                    />
                    {errors.email && <p className="text-xs text-destructive mt-1 uppercase tracking-tight font-medium" role="alert">{errors.email.message}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="organization" className="block text-xs font-medium text-bloom-muted mb-1 uppercase tracking-wider">Organization *</label>
                  <input
                    id="organization"
                    {...register('organization')}
                    aria-required="true"
                    aria-invalid={errors.organization ? 'true' : 'false'}
                    className="w-full px-4 py-3 rounded-lg border border-bloom-border bg-bloom-cream/20 font-sans text-sm focus-bloom"
                  />
                  {errors.organization && <p className="text-xs text-destructive mt-1 uppercase tracking-tight font-medium" role="alert">{errors.organization.message}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="orgType" className="block text-xs font-medium text-bloom-muted mb-1 uppercase tracking-wider">Type *</label>
                    <select
                      id="orgType"
                      {...register('orgType')}
                      aria-required="true"
                      aria-invalid={errors.orgType ? 'true' : 'false'}
                      className="w-full px-4 py-3 rounded-lg border border-bloom-border bg-bloom-cream/20 font-sans text-sm focus-bloom"
                    >
                      <option value="">Select type</option>
                      <option value="nonprofit">Nonprofit</option>
                      <option value="government">Government</option>
                      <option value="hoa">HOA</option>
                      <option value="church">Church</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.orgType && <p className="text-xs text-destructive mt-1 uppercase tracking-tight font-medium" role="alert">{errors.orgType.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="budget" className="block text-xs font-medium text-bloom-muted mb-1 uppercase tracking-wider">Budget *</label>
                    <select
                      id="budget"
                      {...register('budget')}
                      aria-required="true"
                      aria-invalid={errors.budget ? 'true' : 'false'}
                      className="w-full px-4 py-3 rounded-lg border border-bloom-border bg-bloom-cream/20 font-sans text-sm focus-bloom"
                    >
                      <option value="">Select range</option>
                      <option value="under-2500">Under $2,500</option>
                      <option value="2500-5000">$2,500–$5,000</option>
                      <option value="5000-10000">$5,000–$10,000</option>
                      <option value="10000+">$10,000+</option>
                    </select>
                    {errors.budget && <p className="text-xs text-destructive mt-1 uppercase tracking-tight font-medium" role="alert">{errors.budget.message}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-bloom-muted mb-1 uppercase tracking-wider">Message *</label>
                  <textarea
                    id="message"
                    rows={4}
                    {...register('message')}
                    aria-required="true"
                    aria-invalid={errors.message ? 'true' : 'false'}
                    className="w-full px-4 py-3 rounded-lg border border-bloom-border bg-bloom-cream/20 font-sans text-sm focus-bloom resize-none"
                    placeholder="Tell us about the project and the impact you want to make..."
                  />
                  {errors.message && <p className="text-xs text-destructive mt-1 uppercase tracking-tight font-medium" role="alert">{errors.message.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-xl bg-bloom-green-deep text-primary-foreground font-sans font-bold text-sm tracking-wide hover:bg-black transition-all disabled:opacity-50 shadow-md focus-bloom"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  </>
);
}

export default function BookPage() {
  usePageTitle('Book a Consultation — Laurel Web Co.');

  const bookSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Book a Consultation",
    "description": "Select a time for a 30-minute consultation or reach out with a direct message.",
    "publisher": {
      "@type": "Organization",
      "@id": "https://laurelweb.co/#organization"
    }
  };

  return (
    <Layout>
      <JsonLd schema={bookSchema} />
      <section 
        className="bg-bloom-green-deep min-h-[40vh] pt-40 pb-24 flex items-center"
        aria-label="Booking introduction"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-6 h-[2px] bg-bloom-gold flex-shrink-0" aria-hidden="true" />
              <span className="font-sans font-medium uppercase tracking-[0.15em] text-bloom-gold text-xs">
                Book a Call
              </span>
            </div>
            <h1 className="heading-hero text-primary-foreground mb-6">
              Let's talk about your goals.
            </h1>
            <p className="font-sans text-lg text-primary-foreground/90 leading-relaxed">
              Select a time for a 30-minute discovery call or send us a quick message to get started.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-bloom-cream py-24 min-h-screen" aria-label="Booking options">
        <div className="container mx-auto px-6">
          <Suspense fallback={<div className="min-h-[400px] flex items-center justify-center font-sans text-bloom-muted">Loading options...</div>}>
            <BookContent />
          </Suspense>
        </div>
      </section>
    </Layout>
  );
}
